/**
 * InteractionManager - Handles selection and drag functionality for meshes
 *
 * Features:
 * - Single and multi-select with Ctrl/Shift modifiers
 * - Visual selection feedback via HighlightLayer
 * - SixDOFDragBehavior for intent-based lift (lateral = slide, upward = lift)
 * - Grab-point-aware tilt (edge grabs tilt more than center grabs)
 * - Ghost mode during drag to prevent bulldozing
 * - Throw physics on release with proportional spin
 * - Camera locking during drag to prevent accidental orbiting
 */

import {
  type AbstractMesh,
  type ArcRotateCamera,
  Color3,
  Color4,
  HighlightLayer,
  type Mesh,
  type Observer,
  PBRMaterial,
  PointerEventTypes,
  type PointerInfo,
  Quaternion,
  type Scene,
  SixDofDragBehavior,
  Vector3,
} from "@babylonjs/core";
import { getLogger } from "../../utils/log.js";

// ─────────────────────────────────────────────────────────────────
// Debug logging — enable with localStorage.debug = "tony:babylon:interaction"
// ─────────────────────────────────────────────────────────────────
const log = getLogger("interaction");

function logInteraction(
  event: string,
  details?: Record<string, unknown>,
): void {
  if (!log.enabled) return;
  const detailStr = details
    ? ` | ${Object.entries(details)
        .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
        .join(", ")}`
    : "";
  log(`${event}${detailStr}`);
}

function getCameraInfo(scene: Scene | null): Record<string, unknown> {
  if (!scene?.activeCamera) return { camera: "none" };
  const cam = scene.activeCamera as ArcRotateCamera;
  if (cam.alpha !== undefined) {
    return {
      alpha: cam.alpha?.toFixed(2),
      attached: !!(cam as ArcRotateCamera).inputs?.attachedToElement,
      beta: cam.beta?.toFixed(2),
      radius: cam.radius?.toFixed(2),
    };
  }
  return { camera: cam.name, type: cam.getClassName() };
}

import { BaseManager } from "../core/BaseManager.js";
import type { PhysicsManager } from "../physics/PhysicsManager.js";
import type {
  ConstraintConfig,
  DragConfig,
  GrabContext,
  IntentConfig,
  InteractionOptions,
  RestDetectionState,
  RotationCorrectionState,
  ThrowGesture,
  VelocityConfig,
  VelocityTrackingState,
  VisualEffectConfig,
} from "./types.js";

// ─────────────────────────────────────────────────────────────────
// Default Visual Effect Configuration
// ─────────────────────────────────────────────────────────────────

const DEFAULT_HOVER_EFFECTS: VisualEffectConfig = {
  edgeRenderer: true, // Best for faceted geometry (gems)
  halo: false,
  incandescent: false,
  outlineEdges: false, // Breaks on faceted geometry
};

const DEFAULT_SELECTION_EFFECTS: VisualEffectConfig = {
  edgeRenderer: false,
  halo: false,
  incandescent: false,
  outlineEdges: false,
};

// ─────────────────────────────────────────────────────────────────
// Configuration Constants
// ─────────────────────────────────────────────────────────────────

/**
 * Drag behavior configuration for SixDOFDragBehavior + grab-point tilt
 */
const DRAG_CONFIG: DragConfig = {
  dragDeltaRatio: 0.8, // Smoothing (0.2 = heavy/sticky, 0.8 = responsive, 1.0 = instant)
  edgeThreshold: 0.5, // @deprecated - use proportional r^3 curve instead
  maxThrowVel: 10.0, // Reduced from 20.0 - prevents orbit launches
  maxTiltAngle: Math.PI / 6, // 30° max tilt for edge grabs
  restAngularThreshold: 0.1, // rad/s - angular velocity below which object is "nearly at rest"
  restFrameCount: 10, // Consecutive frames below threshold to confirm rest

  // Rest detection for gradual rotation correction
  restLinearThreshold: 0.05, // m/s - linear velocity below which object is "nearly at rest"
  rotationCorrectionTime: 300, // ms - duration of gradual slerp correction
  throwForce: 6.0, // Reduced from 15.0 - more controlled throws
  zDragFactor: 0.5, // Controls lift feel (0 = no lift, 0.5 = heavy, 1.0 = 1:1)
};

/**
 * Velocity tracking configuration for realistic throw physics
 * Uses time-based sampling, peak velocity retention, and gesture detection
 */
const VELOCITY_CONFIG: VelocityConfig = {
  flickThreshold: 5.0, // m/s - above this = "flick" gesture (full momentum)
  minThrowSpeed: 1.5, // m/s - below this = "place" gesture (gentle drop)
  peakBlendFactor: 0.6, // How much peak velocity influences final throw (0-1)
  peakDecayHalfLife: 150, // ms - peak velocity fades over this half-life
  placeBoostFactor: 0.3, // Small boost in drag direction for "place" gesture
  sampleCount: 8, // More samples for better velocity estimation
  tossThreshold: 2.5, // m/s - above this = "toss" gesture (moderate momentum)
  velocityWindowMs: 100, // Calculate velocity over last 100ms of drag
};

/**
 * Intent classification configuration for surface vs lift mode detection
 * @reserved For future virtual hand + constraint implementation
 */
const _INTENT_CONFIG: IntentConfig = {
  classificationWindowMs: 100, // 100ms classification window
  directionRatio: 1.5, // Ratio for direction dominance
  ghostModeHeightThreshold: 0.5, // Enable ghost when 0.5 units above surface
  lateralThreshold: 8, // 8 pixels lateral to confirm surface
  longPressThresholdMs: 200, // 200ms long-press for touch lift
  surfaceHoverOffset: 0.1, // Small hover above table in surface mode
  upwardThreshold: 6, // 6 pixels upward to confirm lift
};

/**
 * Constraint configuration for surface drag mode (tight, responsive)
 * @reserved For future virtual hand + constraint implementation
 */
const _SURFACE_CONSTRAINT: ConstraintConfig = {
  angularDamping: 30,
  angularStiffness: 100, // Looser rotation
  linearDamping: 200,
  linearStiffness: 2000, // Very tight - close follow
};

/**
 * Constraint configuration for lift mode (softer, heavier feel)
 * @reserved For future virtual hand + constraint implementation
 */
const _LIFT_CONSTRAINT: ConstraintConfig = {
  angularDamping: 40,
  angularStiffness: 150, // Tighter to prevent wild spinning
  linearDamping: 150,
  linearStiffness: 800, // Slightly softer - feels "heavier"
};

// Export reserved configs to prevent lint errors while keeping for future use

/**
 * Manager for selection and drag interactions on meshes
 */
export class InteractionManager extends BaseManager {
  private scene: Scene | null = null;
  private physicsManager: PhysicsManager | null = null;
  private highlightLayer: HighlightLayer | null = null;
  private pointerObserver: Observer<PointerInfo> | null = null;

  // Selection state
  private selectedMeshes: Set<Mesh> = new Set();
  private selectionColor: Color3 = Color3.Teal();

  // Drag behavior state
  private behaviorMap = new Map<number, SixDofDragBehavior>();
  private dragContextMap = new Map<number, GrabContext>();
  private lastPositionsMap = new Map<number, Vector3[]>();
  // Track which behaviors have actually started dragging (vs just attached)
  private dragStartedMap = new Set<number>();

  // Hover state for edge highlighting
  private hoveredMesh: Mesh | null = null;

  // Rest detection and rotation correction state
  private restStates = new Map<number, RestDetectionState>();
  private correctionStates = new Map<number, RotationCorrectionState>();
  private beforeRenderObserver: Observer<Scene> | null = null;

  // Original damping values (for restoration after drag)
  private originalDampingMap = new Map<
    number,
    { linear: number; angular: number }
  >();

  // Velocity tracking for realistic throws (time-based, peak retention)
  private velocityStates = new Map<number, VelocityTrackingState>();

  // Frame-rate compensation observers (one per active drag)
  private frameRateCompensationObservers = new Map<number, Observer<Scene>>();

  // Options with effect configuration
  private options: Required<InteractionOptions> & {
    hoverEffects: VisualEffectConfig;
    selectionEffects: VisualEffectConfig;
  } = {
    detachCameraOnDrag: true,
    enableDrag: true,
    enableSelection: true,
    hoverEffects: { ...DEFAULT_HOVER_EFFECTS },
    selectionEffects: { ...DEFAULT_SELECTION_EFFECTS },
  };

  protected async onInitialize(): Promise<void> {
    // No async work needed
  }

  protected onDispose(): void {
    // Remove all drag behaviors
    for (const [meshId, behavior] of this.behaviorMap) {
      const mesh = this.scene?.getMeshById(String(meshId));
      if (mesh) {
        mesh.removeBehavior(behavior);
      }
    }
    this.behaviorMap.clear();
    this.dragContextMap.clear();
    this.lastPositionsMap.clear();
    this.dragStartedMap.clear();

    // Remove frame-rate compensation observers
    for (const observer of this.frameRateCompensationObservers.values()) {
      this.scene?.onAfterRenderObservable.remove(observer);
    }
    this.frameRateCompensationObservers.clear();

    // Remove pointer observer
    if (this.pointerObserver && this.scene) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }

    // Dispose highlight layer
    if (this.highlightLayer) {
      this.highlightLayer.dispose();
      this.highlightLayer = null;
    }

    // Clear selection
    this.selectedMeshes.clear();

    this.scene = null;
    this.physicsManager = null;
  }

  /**
   * Set the scene reference and create the highlight layer
   */
  setScene(scene: Scene): void {
    this.scene = scene;

    // Create highlight layer for selection visuals
    this.highlightLayer = new HighlightLayer("selection_hl", scene);
    this.highlightLayer.innerGlow = false;
    this.highlightLayer.outerGlow = true;
  }

  /**
   * Set the theme-aware selection color
   * Called from React with theme context
   */
  setSelectionColor(color: Color3): void {
    this.selectionColor = color;

    // Re-apply effects to existing selections with new color
    for (const mesh of this.selectedMeshes) {
      this.removeVisualEffects(mesh);
      this.applyVisualEffects(mesh, this.options.selectionEffects, color);
    }
  }

  /**
   * Update hover effect configuration at runtime
   * Effects: halo (HighlightLayer), outlineEdges (renderOutline), incandescent (renderOverlay)
   */
  setHoverEffects(effects: Partial<VisualEffectConfig>): void {
    this.options.hoverEffects = { ...this.options.hoverEffects, ...effects };

    // Re-apply to currently hovered mesh
    if (this.hoveredMesh && !this.selectedMeshes.has(this.hoveredMesh)) {
      this.removeVisualEffects(this.hoveredMesh);
      this.applyVisualEffects(
        this.hoveredMesh,
        this.options.hoverEffects,
        this.selectionColor,
      );
    }
  }

  /**
   * Update selection effect configuration at runtime
   * Effects: halo (HighlightLayer), outlineEdges (renderOutline), incandescent (renderOverlay)
   */
  setSelectionEffects(effects: Partial<VisualEffectConfig>): void {
    this.options.selectionEffects = {
      ...this.options.selectionEffects,
      ...effects,
    };

    // Re-apply to all selected meshes
    for (const mesh of this.selectedMeshes) {
      this.removeVisualEffects(mesh);
      this.applyVisualEffects(
        mesh,
        this.options.selectionEffects,
        this.selectionColor,
      );
    }
  }

  /**
   * Get current hover effects configuration
   */
  getHoverEffects(): VisualEffectConfig {
    return { ...this.options.hoverEffects };
  }

  /**
   * Get current selection effects configuration
   */
  getSelectionEffects(): VisualEffectConfig {
    return { ...this.options.selectionEffects };
  }

  /**
   * Enable interactions with the given physics manager
   */
  enableInteractions(
    physicsManager: PhysicsManager,
    options?: InteractionOptions,
  ): void {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene() first.");
    }

    this.physicsManager = physicsManager;

    // Merge options with proper handling of nested visual effect configs
    if (options) {
      this.options = {
        ...this.options,
        detachCameraOnDrag:
          options.detachCameraOnDrag ?? this.options.detachCameraOnDrag,
        enableDrag: options.enableDrag ?? this.options.enableDrag,
        enableSelection:
          options.enableSelection ?? this.options.enableSelection,
        hoverEffects: {
          ...this.options.hoverEffects,
          ...options.hoverEffects,
        },
        selectionEffects: {
          ...this.options.selectionEffects,
          ...options.selectionEffects,
        },
      };
    }

    // Setup pointer observable
    this.pointerObserver = this.scene.onPointerObservable.add((pointerInfo) => {
      this.handlePointer(pointerInfo);
    });

    logInteraction("INTERACTIONS_ENABLED", {
      ...getCameraInfo(this.scene),
      options: this.options,
    });
    this.emit("interactionsEnabled", { options: this.options });
  }

  /**
   * Disable interactions and cleanup
   */
  disableInteractions(): void {
    if (this.pointerObserver && this.scene) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }

    this.clearSelection();
    this.physicsManager = null;

    this.emit("interactionsDisabled", {});
  }

  /**
   * Get currently selected meshes
   */
  getSelectedMeshes(): Mesh[] {
    return [...this.selectedMeshes];
  }

  /**
   * Check if a mesh is selected
   */
  isSelected(mesh: Mesh): boolean {
    return this.selectedMeshes.has(mesh);
  }

  /**
   * Programmatically select a mesh
   */
  selectMesh(mesh: Mesh, additive = false): void {
    if (!additive) {
      this.clearSelection();
    }
    this.select(mesh);
  }

  /**
   * Programmatically deselect a mesh
   */
  deselectMesh(mesh: Mesh): void {
    this.deselect(mesh);
  }

  /**
   * Clear all selections
   */
  clearAllSelections(): void {
    this.clearSelection();
  }

  /**
   * Clear hover state (removes visual effects from hovered mesh)
   */
  clearHover(): void {
    if (this.hoveredMesh) {
      this.removeVisualEffects(this.hoveredMesh);
      this.hoveredMesh = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Private: Pointer Handling
  // ─────────────────────────────────────────────────────────────────

  private handlePointer(pointerInfo: PointerInfo): void {
    switch (pointerInfo.type) {
      case PointerEventTypes.POINTERDOWN:
        this.handlePointerDown(pointerInfo);
        break;
      case PointerEventTypes.POINTERMOVE:
        this.handlePointerMove(pointerInfo);
        break;
      case PointerEventTypes.POINTERUP:
        this.handlePointerUp();
        break;
    }
  }

  private handlePointerDown(pointerInfo: PointerInfo): void {
    if (!this.scene || pointerInfo.event.button !== 0) {
      logInteraction("POINTER_DOWN_IGNORED", {
        button: pointerInfo.event.button,
        reason: !this.scene ? "no_scene" : "not_left_click",
      });
      return;
    }

    const pickResult = pointerInfo.pickInfo;
    const hitMesh = pickResult?.pickedMesh;

    // Traverse up hierarchy to find physics root (handles child mesh clicks)
    const physicsRoot = hitMesh ? this.findPhysicsRoot(hitMesh) : null;

    logInteraction("POINTER_DOWN", {
      distance: pickResult?.distance?.toFixed(2) ?? "none",
      hitMesh: hitMesh?.name ?? "none",
      hitMeshId: hitMesh?.uniqueId ?? "none",
      physicsRoot: physicsRoot?.name ?? "none",
      physicsRootId: physicsRoot?.uniqueId ?? "none",
      pickedPoint: pickResult?.pickedPoint
        ? `(${pickResult.pickedPoint.x.toFixed(2)}, ${pickResult.pickedPoint.y.toFixed(2)}, ${pickResult.pickedPoint.z.toFixed(2)})`
        : "none",
      traversedUp: hitMesh !== physicsRoot,
      ...getCameraInfo(this.scene),
      behaviorsActive: this.behaviorMap.size,
      selectedCount: this.selectedMeshes.size,
    });

    if (physicsRoot) {
      logInteraction("HIT_PHYSICS_ROOT", {
        hitChild:
          hitMesh?.name !== physicsRoot.name ? hitMesh?.name : undefined,
        mesh: physicsRoot.name,
        meshId: physicsRoot.uniqueId,
        position: `(${physicsRoot.position.x.toFixed(2)}, ${physicsRoot.position.y.toFixed(2)}, ${physicsRoot.position.z.toFixed(2)})`,
      });

      // 1. SELECTION LOGIC
      if (this.options.enableSelection) {
        const isMultiSelect =
          pointerInfo.event.ctrlKey || pointerInfo.event.shiftKey;

        if (isMultiSelect) {
          if (this.selectedMeshes.has(physicsRoot)) {
            logInteraction("DESELECT_MULTI", { mesh: physicsRoot.name });
            this.deselect(physicsRoot);
            this.reattachCamera();
            return;
          }
          logInteraction("SELECT_MULTI", { mesh: physicsRoot.name });
          this.select(physicsRoot);
        } else {
          if (!this.selectedMeshes.has(physicsRoot)) {
            logInteraction("SELECT_SINGLE", {
              clearingPrevious: this.selectedMeshes.size,
              mesh: physicsRoot.name,
            });
            this.clearSelection();
            this.select(physicsRoot);
          } else {
            logInteraction("ALREADY_SELECTED", { mesh: physicsRoot.name });
          }
        }
      }

      // 2. ATTACH DRAG BEHAVIOR (if enabled)
      if (this.options.enableDrag) {
        const grabPoint =
          pickResult?.pickedPoint ?? physicsRoot.getAbsolutePosition();
        logInteraction("ATTACHING_DRAG_BEHAVIOR", {
          grabPoint: `(${grabPoint.x.toFixed(2)}, ${grabPoint.y.toFixed(2)}, ${grabPoint.z.toFixed(2)})`,
          mesh: physicsRoot.name,
          usedFallback: !pickResult?.pickedPoint,
        });
        this.attachDragBehavior(physicsRoot, grabPoint);
      }
    } else {
      logInteraction("HIT_EMPTY_OR_NON_PHYSICS", {
        hitMesh: hitMesh?.name ?? "none",
        metadata: hitMesh?.metadata ?? "none",
      });
      if (!pointerInfo.event.ctrlKey) {
        this.clearSelection();
      }
    }
  }

  private handlePointerMove(_pointerInfo: PointerInfo): void {
    // Skip hover detection during active drag
    if (this.behaviorMap.size > 0 || !this.scene) return;

    // Manually pick since pickInfo isn't populated on POINTERMOVE
    const pickResult = this.scene.pick(
      this.scene.pointerX,
      this.scene.pointerY,
    );
    const hitMesh = pickResult?.pickedMesh;
    const physicsRoot = hitMesh ? this.findPhysicsRoot(hitMesh) : null;

    // Same mesh - no change needed
    if (physicsRoot === this.hoveredMesh) return;

    // Remove hover effects from previous mesh (if not selected)
    if (this.hoveredMesh && !this.selectedMeshes.has(this.hoveredMesh)) {
      this.removeVisualEffects(this.hoveredMesh);
    }
    this.hoveredMesh = null;

    // Add hover effects to new mesh (if interactive and not selected)
    if (physicsRoot && !this.selectedMeshes.has(physicsRoot)) {
      this.applyVisualEffects(
        physicsRoot,
        this.options.hoverEffects,
        this.selectionColor,
      );
      this.hoveredMesh = physicsRoot;
    }
  }

  private handlePointerUp(): void {
    logInteraction("POINTER_UP", {
      behaviorsAttached: this.behaviorMap.size,
      dragsStarted: this.dragStartedMap.size,
      ...getCameraInfo(this.scene),
    });

    // Clean up behaviors that were attached but never actually started dragging
    const staleBehaviors: number[] = [];
    for (const [meshId, behavior] of this.behaviorMap) {
      if (!this.dragStartedMap.has(meshId)) {
        staleBehaviors.push(meshId);
        const mesh = this.scene?.getMeshById(String(meshId)) as Mesh | null;
        logInteraction("CLEANUP_STALE_BEHAVIOR", {
          meshId,
          meshName: mesh?.name ?? "unknown",
          reason: "never_started_dragging",
        });
        if (mesh) {
          mesh.removeBehavior(behavior);
        }
        this.behaviorMap.delete(meshId);
        this.dragContextMap.delete(meshId);
        this.lastPositionsMap.delete(meshId);
      }
    }

    if (staleBehaviors.length === 0 && this.behaviorMap.size > 0) {
      logInteraction("ACTIVE_DRAGS_REMAIN", {
        count: this.behaviorMap.size,
        meshIds: [...this.behaviorMap.keys()],
      });
    }

    // Reattach camera if no active drags
    if (this.behaviorMap.size === 0) {
      logInteraction(
        "NO_ACTIVE_DRAGS_REATTACHING_CAMERA",
        getCameraInfo(this.scene),
      );
      this.reattachCamera();
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Private: Visual Effects
  // ─────────────────────────────────────────────────────────────────

  /**
   * Apply visual effects to a mesh based on configuration
   * Each effect type maps to a different Babylon.js rendering system:
   * - edgeRenderer: EdgeRenderer geometric edges (best for faceted meshes)
   * - halo: HighlightLayer gaussian glow (GPU post-processing)
   * - outlineEdges: renderOutline silhouette (OutlineRenderer)
   * - incandescent: renderOverlay color wash (additive blend)
   */
  private applyVisualEffects(
    mesh: Mesh,
    config: VisualEffectConfig,
    color: Color3,
  ): void {
    // EdgeRenderer: geometric edge detection (best for faceted meshes like gems)
    // Uses face normal angles to detect edges, not vertex normal scaling
    // Apply to root (if visible) and all visible children
    if (config.edgeRenderer) {
      const applyEdges = (m: Mesh) => {
        if (!m.isVisible) return; // Skip invisible meshes (like GLB root containers)
        if (!m.edgesRenderer) {
          m.enableEdgesRendering(0.9999); // High epsilon = show all facet edges
        }
        m.edgesWidth = 2.0;
        m.edgesColor = new Color4(color.r, color.g, color.b, 0.9);
      };
      applyEdges(mesh);
      for (const child of mesh.getChildMeshes()) {
        if (this.isMesh(child)) {
          applyEdges(child);
        }
      }
    }

    // Outline: apply to parent + all children (original working implementation)
    // Also disable needDepthPrePass on PBR materials to prevent black faces
    if (config.outlineEdges) {
      // Adjust outline width based on world scale to maintain consistent visual thickness
      // The shader applies offset in local space, then world transform scales it
      // Use max scale to handle non-uniform scaling (outline expands along all axes)
      const worldScale = mesh.absoluteScaling;
      const maxScale = Math.max(worldScale.x, worldScale.y, worldScale.z);
      const outlineWidth = 0.05 / Math.max(maxScale, 0.01);

      mesh.renderOutline = true;
      mesh.outlineColor = color;
      mesh.outlineWidth = outlineWidth;
      if (mesh.material instanceof PBRMaterial) {
        mesh.material.needDepthPrePass = false;
      }
      for (const child of mesh.getChildMeshes()) {
        if (this.isMesh(child)) {
          child.renderOutline = true;
          child.outlineColor = color;
          child.outlineWidth = outlineWidth;
          if (child.material instanceof PBRMaterial) {
            child.material.needDepthPrePass = false;
          }
        }
      }
    }

    // Incandescent: overlay color wash
    if (config.incandescent) {
      mesh.renderOverlay = true;
      mesh.overlayColor = color;
      mesh.overlayAlpha = 0.3;
    }

    // Halo: HighlightLayer handles parent+children correctly
    if (config.halo && this.highlightLayer) {
      this.highlightLayer.addMesh(mesh, color);
      for (const child of mesh.getChildMeshes()) {
        if (this.isMesh(child)) {
          this.highlightLayer.addMesh(child, color);
        }
      }
    }
  }

  /**
   * Remove all visual effects from a mesh
   */
  private removeVisualEffects(mesh: Mesh): void {
    // EdgeRenderer: hide by setting width to 0 (cheaper than dispose/recreate)
    // Apply to root and children (mirrors applyVisualEffects)
    if (mesh.edgesRenderer) {
      mesh.edgesWidth = 0;
    }
    for (const child of mesh.getChildMeshes()) {
      if (this.isMesh(child) && child.edgesRenderer) {
        child.edgesWidth = 0;
      }
    }

    // Outline and overlay: root and children
    mesh.renderOutline = false;
    mesh.renderOverlay = false;
    // Restore needDepthPrePass for PBR materials (disabled by outline effects)
    if (mesh.material instanceof PBRMaterial) {
      mesh.material.needDepthPrePass = true;
    }
    for (const child of mesh.getChildMeshes()) {
      if (this.isMesh(child)) {
        child.renderOutline = false;
        child.renderOverlay = false;
        if (child.material instanceof PBRMaterial) {
          child.material.needDepthPrePass = true;
        }
      }
    }

    // Halo: remove from parent and children
    this.highlightLayer?.removeMesh(mesh);
    for (const child of mesh.getChildMeshes()) {
      if (this.isMesh(child)) {
        this.highlightLayer?.removeMesh(child);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Private: Selection
  // ─────────────────────────────────────────────────────────────────

  private select(mesh: Mesh): void {
    this.selectedMeshes.add(mesh);

    // Apply selection effects using configurable system
    this.applyVisualEffects(
      mesh,
      this.options.selectionEffects,
      this.selectionColor,
    );

    this.emit("meshSelected", { count: this.selectedMeshes.size, mesh });
  }

  private deselect(mesh: Mesh): void {
    this.selectedMeshes.delete(mesh);

    // Remove all visual effects
    this.removeVisualEffects(mesh);

    this.emit("meshDeselected", { count: this.selectedMeshes.size, mesh });
  }

  private clearSelection(): void {
    for (const mesh of this.selectedMeshes) {
      this.removeVisualEffects(mesh);
    }
    this.selectedMeshes.clear();

    this.emit("selectionCleared", {});
  }

  // ─────────────────────────────────────────────────────────────────
  // Private: SixDOFDragBehavior
  // ─────────────────────────────────────────────────────────────────

  /**
   * Attach SixDofDragBehavior to a mesh for intent-based dragging
   * - Lateral drag = slides on surface
   * - Upward drag = lifts proportionally (zDragFactor)
   * - Edge grabs tilt more than center grabs
   */
  private attachDragBehavior(mesh: Mesh, pickPoint: Vector3): void {
    if (this.behaviorMap.has(mesh.uniqueId)) return;

    // 1. Calculate grab context (where on the mesh we grabbed)
    const grabContext = this.calculateGrabContext(mesh, pickPoint);
    this.dragContextMap.set(mesh.uniqueId, grabContext);
    this.lastPositionsMap.set(mesh.uniqueId, [mesh.position.clone()]);

    // 2. Create SixDofDragBehavior with intent detection
    const dragBehavior = new SixDofDragBehavior();
    dragBehavior.zDragFactor = DRAG_CONFIG.zDragFactor;
    dragBehavior.dragDeltaRatio = DRAG_CONFIG.dragDeltaRatio;

    // 3. Drag Start - prepare physics for manual control
    dragBehavior.onDragStartObservable.add(() => {
      logInteraction("DRAG_START", {
        mesh: mesh.name,
        meshId: mesh.uniqueId,
        position: `(${mesh.position.x.toFixed(2)}, ${mesh.position.y.toFixed(2)}, ${mesh.position.z.toFixed(2)})`,
        ...getCameraInfo(this.scene),
      });

      // Mark this drag as actually started (for cleanup in handlePointerUp)
      this.dragStartedMap.add(mesh.uniqueId);

      // Save original damping values for restoration after drag
      const linearDamping = this.physicsManager?.getLinearDamping(mesh.id);
      const angularDamping = this.physicsManager?.getAngularDamping(mesh.id);
      if (
        linearDamping !== null &&
        linearDamping !== undefined &&
        angularDamping !== null &&
        angularDamping !== undefined
      ) {
        this.originalDampingMap.set(mesh.uniqueId, {
          angular: angularDamping,
          linear: linearDamping,
        });
      }

      // Detach camera to prevent orbiting during drag
      if (this.options.detachCameraOnDrag && this.scene?.activeCamera) {
        logInteraction("CAMERA_DETACH", getCameraInfo(this.scene));
        this.scene.activeCamera.detachControl();
      }

      // Disable pre-step so mesh position isn't overwritten by physics
      this.physicsManager?.setDisablePreStep(mesh.id, true);
      this.physicsManager?.setGhostMode(mesh.id, true);

      // Initialize velocity tracking for realistic throws
      this.initVelocityTracking(mesh);

      // Frame-rate compensation: Use onAfterRenderObservable to set ratio for NEXT frame
      // The behavior's onBeforeRenderObservable runs first (added during attach()),
      // so we update the ratio AFTER each frame, ready for the next frame's smoothing
      const scene = this.scene;
      if (scene) {
        const frameObserver = scene.onAfterRenderObservable.add(() => {
          const deltaTime = scene.getEngine().getDeltaTime();
          const baseRatio = DRAG_CONFIG.dragDeltaRatio;
          // Exponential decay formula: 1 - (1-r)^(dt/16.67)
          // Maintains consistent drag feel across different frame rates
          const newRatio = 1 - (1 - baseRatio) ** (deltaTime / 16.67);
          dragBehavior.dragDeltaRatio = newRatio;
        });
        this.frameRateCompensationObservers.set(mesh.uniqueId, frameObserver);
      }

      this.emit("dragStart", {
        meshes: [mesh],
        startPoint: pickPoint,
      });
    });

    // 4. During Drag - apply grab-point tilt and track velocity
    dragBehavior.onDragObservable.add(() => {
      this.applyGrabPointTilt(mesh, grabContext);
      this.trackVelocity(mesh);

      // Use velocity-based movement instead of setTargetTransform
      // setTargetTransform creates persistent Havok state that overrides throw velocity
      const state = this.velocityStates.get(mesh.uniqueId);
      if (state && state.samples.length >= 2) {
        const recent = state.samples[state.samples.length - 1];
        const previous = state.samples[state.samples.length - 2];
        const dt = (recent.timestamp - previous.timestamp) / 1000;

        if (dt > 0.001) {
          const velocity = recent.position
            .subtract(previous.position)
            .scale(1 / dt);
          this.physicsManager?.setLinearVelocity(mesh.id, velocity);
        }
      }
    });

    // 5. Drag End - restore physics, apply throw, schedule gradual rotation correction
    dragBehavior.onDragEndObservable.add(() => {
      logInteraction("DRAG_END", {
        mesh: mesh.name,
        meshId: mesh.uniqueId,
        position: `(${mesh.position.x.toFixed(2)}, ${mesh.position.y.toFixed(2)}, ${mesh.position.z.toFixed(2)})`,
        ...getCameraInfo(this.scene),
      });

      const context = this.dragContextMap.get(mesh.uniqueId);

      // 1. Apply throw velocity (no setTargetTransform - it creates persistent state)
      this.applyThrow(mesh);

      // 2. Restore physics control - re-enable pre-step and collisions
      this.physicsManager?.setGhostMode(mesh.id, false);
      this.physicsManager?.setDisablePreStep(mesh.id, false);

      // 4. Initialize gradual rotation correction (replaces immediate snap)
      if (context?.initialRotation) {
        this.initializeRestDetection(mesh, context.initialRotation);
      } else {
        this.dragContextMap.delete(mesh.uniqueId);
      }

      // Re-attach camera
      logInteraction("CAMERA_REATTACH_DRAG_END", getCameraInfo(this.scene));
      this.reattachCamera();

      // Cleanup frame-rate compensation observer
      const frameObserver = this.frameRateCompensationObservers.get(
        mesh.uniqueId,
      );
      if (frameObserver && this.scene) {
        this.scene.onAfterRenderObservable.remove(frameObserver);
        this.frameRateCompensationObservers.delete(mesh.uniqueId);
      }

      // Cleanup behavior and position tracking (but keep dragContextMap for correction)
      mesh.removeBehavior(dragBehavior);
      this.behaviorMap.delete(mesh.uniqueId);
      this.lastPositionsMap.delete(mesh.uniqueId);
      this.dragStartedMap.delete(mesh.uniqueId);

      this.emit("dragEnd", { meshes: [mesh] });
    });

    // Attach behavior to mesh
    mesh.addBehavior(dragBehavior);
    this.behaviorMap.set(mesh.uniqueId, dragBehavior);
  }

  /**
   * Calculate grab context - where on the mesh the user grabbed
   * Returns normalized radius (0 = center, 1 = edge) and grab angle
   */
  private calculateGrabContext(
    mesh: Mesh,
    worldPickPoint: Vector3,
  ): GrabContext {
    // Convert world pick point to mesh local space
    const invWorld = mesh.getWorldMatrix().clone().invert();
    const localGrabPoint = Vector3.TransformCoordinates(
      worldPickPoint,
      invWorld,
    );

    // Calculate normalized radius (0 = center, 1 = edge)
    const bounds = mesh.getBoundingInfo().boundingBox;
    const meshRadius = Math.max(bounds.extendSize.x, bounds.extendSize.z);
    const grabDistance = Math.sqrt(
      localGrabPoint.x ** 2 + localGrabPoint.z ** 2,
    );
    const normalizedRadius = Math.min(grabDistance / meshRadius, 1.0);

    // Angle of grab point relative to mesh forward (Z axis)
    const grabAngle = Math.atan2(localGrabPoint.x, localGrabPoint.z);

    return {
      grabAngle,
      initialRotation:
        mesh.rotationQuaternion?.clone() ?? Quaternion.Identity(),
      localGrabPoint,
      mesh,
      normalizedRadius,
    };
  }

  /**
   * Apply tilt based on grab point using continuous proportional function
   * Uses cubic curve (r^3) for natural feel: center very stable, edges responsive
   *
   * At r=0.3: tilt = 2.7% of max (nearly none)
   * At r=0.5: tilt = 12.5% of max (subtle)
   * At r=0.7: tilt = 34% of max (noticeable)
   * At r=1.0: tilt = 100% of max (full)
   */
  private applyGrabPointTilt(mesh: Mesh, context: GrabContext): void {
    const { grabAngle, initialRotation, normalizedRadius } = context;

    // Cubic curve for proportional tilt - no hard threshold
    // This creates a stable center region with progressive tilt toward edges
    const tiltFactor = normalizedRadius ** 3;
    const tiltAmount = tiltFactor * DRAG_CONFIG.maxTiltAngle;

    // Tilt axis perpendicular to grab direction
    // This makes the object "dangle" from the grab point
    const tiltAxis = new Vector3(-Math.sin(grabAngle), 0, Math.cos(grabAngle));

    const tiltQuat = Quaternion.RotationAxis(tiltAxis, tiltAmount);
    mesh.rotationQuaternion = initialRotation.multiply(tiltQuat);
  }

  /**
   * Initialize velocity tracking state for a mesh when drag starts
   */
  private initVelocityTracking(mesh: Mesh): void {
    this.velocityStates.set(mesh.uniqueId, {
      lastDirection: Vector3.Zero(),
      peakSpeed: 0,
      peakTimestamp: performance.now(),
      peakVelocity: Vector3.Zero(),
      samples: [],
    });
  }

  /**
   * Track velocity with timestamps for frame-rate independent calculation
   * Also tracks peak velocity for "flick and pause" gesture retention
   */
  private trackVelocity(mesh: Mesh): void {
    const state = this.velocityStates.get(mesh.uniqueId);
    if (!state) return;

    const now = performance.now();
    const currentPos = mesh.position.clone();

    // Add new sample
    state.samples.push({ position: currentPos, timestamp: now });

    // Keep only samples within the velocity window
    const cutoff = now - VELOCITY_CONFIG.velocityWindowMs;
    while (state.samples.length > 1 && state.samples[0].timestamp < cutoff) {
      state.samples.shift();
    }

    // Cap at sampleCount
    while (state.samples.length > VELOCITY_CONFIG.sampleCount) {
      state.samples.shift();
    }

    // Calculate instantaneous velocity and update peak
    if (state.samples.length >= 2) {
      const recent = state.samples[state.samples.length - 1];
      const previous = state.samples[state.samples.length - 2];
      const dt = (recent.timestamp - previous.timestamp) / 1000; // Convert to seconds

      if (dt > 0.001) {
        // Avoid division by near-zero
        const instantVelocity = recent.position
          .subtract(previous.position)
          .scale(1 / dt);
        const speed = instantVelocity.length();

        // Update last direction if significant movement
        if (speed > 0.1) {
          state.lastDirection = instantVelocity.normalize();
        }

        // Update peak velocity if this is faster
        if (speed > state.peakSpeed) {
          state.peakSpeed = speed;
          state.peakVelocity = instantVelocity.clone();
          state.peakTimestamp = now;
        }
      }
    }

    // Also update legacy position tracking for compatibility
    const positions = this.lastPositionsMap.get(mesh.uniqueId);
    if (positions) {
      positions.push(currentPos);
      if (positions.length > 5) positions.shift();
    }
  }

  /**
   * Classify the throw gesture based on velocity patterns
   */
  private classifyThrowGesture(state: VelocityTrackingState): ThrowGesture {
    // Calculate current velocity from samples
    const currentSpeed = this.calculateCurrentSpeed(state);

    if (currentSpeed >= VELOCITY_CONFIG.flickThreshold) {
      return "flick";
    }
    if (currentSpeed >= VELOCITY_CONFIG.tossThreshold) {
      return "toss";
    }
    return "place";
  }

  /**
   * Calculate current velocity from time-based samples
   * Uses weighted average over the sample window for smoothness
   */
  private calculateCurrentSpeed(state: VelocityTrackingState): number {
    if (state.samples.length < 2) return 0;

    const first = state.samples[0];
    const last = state.samples[state.samples.length - 1];
    const dt = (last.timestamp - first.timestamp) / 1000;

    if (dt < 0.001) return 0;

    const displacement = last.position.subtract(first.position);
    return displacement.length() / dt;
  }

  /**
   * Calculate the final throw velocity with time-based tracking,
   * peak velocity retention, and gesture-based behavior
   */
  private calculateThrowVelocity(state: VelocityTrackingState): Vector3 {
    const now = performance.now();

    // Calculate current velocity from samples
    let currentVelocity = Vector3.Zero();
    if (state.samples.length >= 2) {
      const first = state.samples[0];
      const last = state.samples[state.samples.length - 1];
      const dt = (last.timestamp - first.timestamp) / 1000;

      if (dt > 0.001) {
        currentVelocity = last.position.subtract(first.position).scale(1 / dt);
      }
    }

    // Apply peak velocity decay: blend with decayed peak
    const timeSincePeak = now - state.peakTimestamp;
    const decayFactor = Math.exp(
      (-timeSincePeak * Math.LN2) / VELOCITY_CONFIG.peakDecayHalfLife,
    );

    // Blend current and peak velocities
    const blendedVelocity = Vector3.Lerp(
      currentVelocity,
      state.peakVelocity.scale(decayFactor),
      VELOCITY_CONFIG.peakBlendFactor * decayFactor,
    );

    return blendedVelocity;
  }

  /**
   * Calculate and apply throw velocity on release
   * Uses time-based velocity, peak retention, and gesture detection
   */
  private applyThrow(mesh: Mesh): void {
    const state = this.velocityStates.get(mesh.uniqueId);
    if (!state) {
      // Fall back to legacy behavior if no velocity state
      this.applyThrowLegacy(mesh);
      return;
    }

    const gesture = this.classifyThrowGesture(state);
    let velocity = this.calculateThrowVelocity(state);

    // Apply gesture-specific behavior - velocity is already in world units/sec
    // so we just need minor scaling, not the old frame-based multipliers
    switch (gesture) {
      case "flick":
        // Full momentum - use velocity directly with slight boost
        velocity = velocity.scale(1.2);
        break;

      case "toss":
        // Moderate momentum
        velocity = velocity.scale(0.8);
        break;

      case "place":
        // Gentle drop with small boost in last drag direction
        if (state.lastDirection.length() > 0.1) {
          velocity = state.lastDirection.scale(2.0);
        } else {
          velocity = Vector3.Zero();
        }
        break;
    }

    // Cap velocity to prevent orbit launches
    if (velocity.length() > DRAG_CONFIG.maxThrowVel) {
      velocity.normalize().scaleInPlace(DRAG_CONFIG.maxThrowVel);
    }

    this.physicsManager?.setLinearVelocity(mesh.id, velocity);

    // Add proportional spin based on grab point
    const context = this.dragContextMap.get(mesh.uniqueId);
    if (context && gesture !== "place") {
      const spinScale = context.normalizedRadius * 0.3;
      this.physicsManager?.setAngularVelocity(
        mesh.id,
        new Vector3(velocity.z * spinScale, 0, -velocity.x * spinScale),
      );
    }

    // Cleanup velocity state
    this.velocityStates.delete(mesh.uniqueId);
  }

  /**
   * Legacy throw calculation (fallback if velocity tracking wasn't initialized)
   */
  private applyThrowLegacy(mesh: Mesh): void {
    const positions = this.lastPositionsMap.get(mesh.uniqueId);
    if (!positions || positions.length < 2) return;

    const velocity = positions[positions.length - 1]
      .subtract(positions[positions.length - 2])
      .scale(DRAG_CONFIG.throwForce);

    if (velocity.length() > DRAG_CONFIG.maxThrowVel) {
      velocity.normalize().scaleInPlace(DRAG_CONFIG.maxThrowVel);
    }

    this.physicsManager?.setLinearVelocity(mesh.id, velocity);

    const context = this.dragContextMap.get(mesh.uniqueId);
    if (context) {
      const spinScale = context.normalizedRadius * 0.3;
      this.physicsManager?.setAngularVelocity(
        mesh.id,
        new Vector3(velocity.z * spinScale, 0, -velocity.x * spinScale),
      );
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Private: Helpers
  // ─────────────────────────────────────────────────────────────────

  /**
   * Traverse up the mesh hierarchy to find the physics root
   * This handles clicks on child meshes (e.g., "brilliant_gem" facet geometry)
   * by finding their parent with isPhysicsRoot metadata
   */
  private findPhysicsRoot(mesh: AbstractMesh): Mesh | null {
    let current: AbstractMesh | null = mesh;

    while (current) {
      if (current.metadata?.isPhysicsRoot === true && this.isMesh(current)) {
        return current;
      }
      current = current.parent as AbstractMesh | null;
    }

    return null;
  }

  private reattachCamera(): void {
    if (
      this.options.detachCameraOnDrag &&
      this.scene?.activeCamera &&
      this.scene.getEngine()
    ) {
      const canvas = this.scene.getEngine().getRenderingCanvas();
      if (canvas) {
        this.scene.activeCamera.attachControl(canvas, true);
        logInteraction("CAMERA_REATTACHED", getCameraInfo(this.scene));
      } else {
        logInteraction("CAMERA_REATTACH_FAILED", { reason: "no_canvas" });
      }
    }
  }

  /**
   * Type guard to check if an AbstractMesh is a Mesh
   */
  private isMesh(mesh: AbstractMesh): mesh is Mesh {
    return mesh.getClassName() === "Mesh";
  }

  // ─────────────────────────────────────────────────────────────────
  // Gradual Rotation Correction (after release)
  // ─────────────────────────────────────────────────────────────────

  /**
   * Initialize rest detection for a mesh after drag release
   * This starts monitoring velocity to determine when the object is at rest
   */
  private initializeRestDetection(
    mesh: Mesh,
    targetRotation: Quaternion,
  ): void {
    this.restStates.set(mesh.uniqueId, {
      framesAtRest: 0,
      isAtRest: false,
    });

    // Store target rotation in dragContextMap for later correction
    // We keep the context around for rotation correction
    const existingContext = this.dragContextMap.get(mesh.uniqueId);
    if (existingContext) {
      existingContext.initialRotation = targetRotation;
    }

    this.ensureBeforeRenderObserver();
  }

  /**
   * Ensure we have a beforeRender observer for rest detection and rotation correction
   */
  private ensureBeforeRenderObserver(): void {
    if (this.beforeRenderObserver || !this.scene) return;

    this.beforeRenderObserver = this.scene.onBeforeRenderObservable.add(() => {
      this.updateRestDetection();
      this.updateRotationCorrections();
    });
  }

  /**
   * Check if released objects have come to rest
   * When at rest, begins gradual rotation correction
   */
  private updateRestDetection(): void {
    for (const [meshId, state] of this.restStates) {
      if (state.isAtRest) continue;

      const context = this.dragContextMap.get(meshId);
      if (!context) {
        this.restStates.delete(meshId);
        continue;
      }

      const mesh = context.mesh;
      const linearVel = this.physicsManager?.getLinearVelocity(mesh.id);
      const angularVel = this.physicsManager?.getAngularVelocity(mesh.id);

      if (!linearVel || !angularVel) continue;

      const isSlowEnough =
        linearVel.length() < DRAG_CONFIG.restLinearThreshold &&
        angularVel.length() < DRAG_CONFIG.restAngularThreshold;

      if (isSlowEnough) {
        state.framesAtRest++;

        if (state.framesAtRest >= DRAG_CONFIG.restFrameCount) {
          state.isAtRest = true;
          this.beginRotationCorrection(mesh, context.initialRotation);
        }
      } else {
        state.framesAtRest = 0; // Reset counter if still moving
      }
    }
  }

  /**
   * Begin gradual rotation correction for a mesh that has come to rest
   */
  private beginRotationCorrection(
    mesh: Mesh,
    targetRotation: Quaternion,
  ): void {
    if (!mesh.rotationQuaternion) return;

    this.correctionStates.set(mesh.uniqueId, {
      duration: DRAG_CONFIG.rotationCorrectionTime,
      startRotation: mesh.rotationQuaternion.clone(),
      startTime: performance.now(),
      targetRotation: targetRotation.clone(),
    });
  }

  /**
   * Update ongoing rotation corrections via slerp
   * Uses easeOutCubic for natural deceleration
   */
  private updateRotationCorrections(): void {
    const now = performance.now();
    const completed: number[] = [];

    for (const [meshId, state] of this.correctionStates) {
      const elapsed = now - state.startTime;
      const t = Math.min(elapsed / state.duration, 1.0);

      // Find mesh from dragContextMap
      const context = this.dragContextMap.get(meshId);
      if (!context?.mesh?.rotationQuaternion) {
        completed.push(meshId);
        continue;
      }

      const mesh = context.mesh;

      // Ensure rotationQuaternion exists (Babylon.js meshes may not have one initially)
      if (!mesh.rotationQuaternion) {
        mesh.rotationQuaternion = Quaternion.Identity();
      }

      // easeOutCubic for natural deceleration: t' = 1 - (1-t)^3
      const eased = 1 - (1 - t) ** 3;
      Quaternion.SlerpToRef(
        state.startRotation,
        state.targetRotation,
        eased,
        mesh.rotationQuaternion,
      );

      // Sync physics body rotation during correction
      this.physicsManager?.setTargetTransform(
        mesh.id,
        mesh.position,
        mesh.rotationQuaternion,
      );

      if (t >= 1.0) {
        completed.push(meshId);
      }
    }

    // Cleanup completed corrections
    for (const meshId of completed) {
      // Get context BEFORE deleting (need mesh reference for damping restoration)
      const context = this.dragContextMap.get(meshId);
      const mesh = context?.mesh;

      this.correctionStates.delete(meshId);
      this.restStates.delete(meshId);
      this.dragContextMap.delete(meshId);

      // Restore original damping values
      const originalDamping = this.originalDampingMap.get(meshId);
      if (originalDamping && mesh) {
        this.physicsManager?.setLinearDamping(mesh.id, originalDamping.linear);
        this.physicsManager?.setAngularDamping(
          mesh.id,
          originalDamping.angular,
        );
        this.originalDampingMap.delete(meshId);
      }
    }

    // Remove observer if no more work pending
    if (this.correctionStates.size === 0 && this.restStates.size === 0) {
      this.cleanupBeforeRenderObserver();
    }
  }

  /**
   * Remove the beforeRender observer when no longer needed
   */
  private cleanupBeforeRenderObserver(): void {
    if (this.beforeRenderObserver && this.scene) {
      this.scene.onBeforeRenderObservable.remove(this.beforeRenderObserver);
      this.beforeRenderObserver = null;
    }
  }
}
